import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Briefcase, X, Check } from "lucide-react";
import Footer from "../../components/Footer";

const heroImage =
  "https://res.cloudinary.com/dpn3bkjba/image/upload/v1768397134/about_image_zjzvgx.png";

const HR_EMAIL = "sales@syntherasecurity.com";

/* ================= JOB DATA ================= */

const jobs = [
  {
    isOpen: false, // <-- Change this to true when you want to show the job again
    title: "Technical Consulting Engineer",
    location: "India / Remote",
    experience: "0-1 years",
    employmentType: "Full-time",
    function: "Technical Consulting / Pre-Sales / Cybersecurity",

    shortDesc:
      "Work at the intersection of cybersecurity engineering, customer consulting, pre-sales, competitive intelligence, and technical content.",

    companyDesc: [
      "Synthera Security is a cybersecurity consulting and managed security company helping organizations design, deploy, and operate modern security architectures across Zero Trust, SASE/SSE, Cloud Security, Data Security, Identity Security, and AI Security.",
      "We work closely with customers to understand their security challenges, architect solutions, demonstrate technologies, and help them move from strategy to implementation.",
      "We are looking for a Technical Consulting Engineer who can operate at the intersection of cybersecurity engineering, customer consulting, pre-sales, competitive intelligence, and technical content.",
      "This is a hands-on role. You will not just create presentations—you will build demos, test technologies, research competitors, participate in customer conversations, and help translate complex security concepts into practical solutions.",
    ],

    responsibilities: [
      {
        title: "1. Customer Demos & Technical Pre-Sales",
        items: [
          "Build and deliver compelling technical demonstrations of cybersecurity solutions.",
          "Conduct discovery sessions with customers to understand their security architecture, challenges, and requirements.",
          "Present technical solutions to security, infrastructure, networking, and IT teams.",
          "Support customer workshops, technical evaluations, POCs, RFPs, and RFIs.",
          "Translate customer requirements into practical security architectures and solution recommendations.",
          "Explain complex security concepts clearly to both technical and non-technical audiences.",
          "Support sales teams during customer meetings and help move opportunities from technical evaluation to decision.",
        ],
      },
      {
        title: "2. Security Architecture & Solution Consulting",
        items: [
          "Develop solution architectures around areas such as Zero Trust, SASE/SSE, Secure Access, Cloud Security, Data Security, Identity, Browser Security, and AI Security.",
          "Understand how different security technologies integrate across the enterprise security stack.",
          "Evaluate customer environments and identify security gaps and opportunities.",
          "Help customers understand technology trade-offs, deployment models, and implementation considerations.",
          "Build reference architectures, technical diagrams, solution briefs, and deployment guides.",
        ],
      },
      {
        title: "3. Competitive Intelligence & Technical Research",
        items: [
          "Research cybersecurity vendors, products, architectures, features, and roadmaps.",
          "Conduct hands-on competitive testing rather than relying only on vendor documentation.",
          "Build competitive comparison matrices, battlecards, and technical assessments.",
          "Identify competitive strengths, weaknesses, gaps, and differentiators.",
          "Track emerging technologies and threats across SASE, SSE, Zero Trust, enterprise browsers, AI security, agentic AI, data security, and cloud security.",
          "Convert technical findings into actionable insights for Sales, Product, and Leadership.",
        ],
      },
      {
        title: "4. Technical Content & Thought Leadership",
        items: [
          "Write technical blogs, solution briefs, whitepapers, battlecards, technical articles, and customer-facing content.",
          "Research emerging cybersecurity topics and turn them into clear technical narratives.",
          "Contribute to Synthera's technical thought leadership across cybersecurity and AI security.",
          "Help develop demo scripts, presentations, webinars, and technical enablement material.",
          "Review technical content for accuracy, clarity, and practical relevance.",
        ],
      },
      {
        title: "5. Labs, POCs & Technology Evaluation",
        items: [
          "Build hands-on security labs and demo environments.",
          "Perform POCs and technical evaluations of security products.",
          "Reproduce security scenarios and vulnerabilities in controlled environments.",
          "Test product capabilities, integrations, policies, APIs, and deployment models.",
          "Develop repeatable demos and technical proof points for customer engagements.",
          "Document findings and turn successful experiments into reusable assets.",
        ],
      },
      {
        title: "6. AI & Emerging Security Technologies",
        items: [
          "Research emerging areas including Generative AI Security, Shadow AI, Agentic AI, AI Governance, AI Guardrails, MCP Security, and AI-enabled attacks.",
          "Evaluate how AI changes enterprise security architectures and attack surfaces.",
          "Develop demonstrations and technical content around emerging AI-security use cases.",
          "Stay current with new security technologies, vulnerabilities, and industry developments.",
        ],
      },
      {
        title: "7. Sales & Internal Enablement",
        items: [
          "Work closely with Sales to prepare for customer meetings and opportunities.",
          "Create technical presentations, discovery questions, demo flows, and solution narratives.",
          "Enable sales teams with competitive intelligence and technical positioning.",
          "Participate in partner and vendor enablement sessions.",
          "Help build a reusable technical knowledge base for Synthera.",
        ],
      },
    ],

    required: [
      "0-1 years of experience in cybersecurity, networking, cloud security, systems engineering, technical pre-sales, or a related technical role.",
      "Strong understanding of fundamental cybersecurity concepts.",
      "Good understanding of networking concepts such as TCP/IP, DNS, HTTP/HTTPS, TLS, proxies, VPNs, firewalls, and identity/access.",
      "Familiarity with cloud environments such as AWS, Azure, or GCP.",
      "Ability to understand and explain security architectures.",
      "Strong communication and presentation skills.",
      "Strong technical research and analytical ability.",
      "Ability to work hands-on with security products and build technical demos.",
      "Strong written communication skills.",
      "Comfortable speaking with customers and presenting technical concepts.",
    ],

    goodToHave: [
      "Experience in cybersecurity pre-sales, solution engineering, consulting, or technical marketing.",
      "Exposure to SASE/SSE, Zero Trust, ZTNA, SWG, CASB, DLP, IAM, EDR/XDR, cloud security, or enterprise browsers.",
      "Experience with security testing, labs, POCs, or vulnerability research.",
      "Experience using tools such as Burp Suite, Wireshark, Nmap, Postman, browser developer tools, or similar security/networking tools.",
      "Basic scripting knowledge in Python, JavaScript, Bash, or PowerShell.",
      "Familiarity with APIs and integrations.",
      "Interest in Generative AI, Agentic AI, and AI security.",
      "Experience creating technical content, blogs, presentations, or documentation.",
    ],

    idealCandidate: {
      intro: "You are someone who can go from:",
      journey:
        "Research → Build a lab → Test it → Understand the architecture → Create a demo → Explain it to a customer → Compare it against competitors → Write about it.",
      description:
        "You enjoy going deep technically, but you can also communicate that knowledge clearly.",
      closing:
        "You don't need to know every cybersecurity technology on day one. We value curiosity, technical depth, ownership, and the ability to learn quickly.",
    },

    successLooksLike: [
      "Independently deliver core technical demos.",
      "Participate confidently in customer discovery and technical discussions.",
      "Build and troubleshoot demo/POC environments.",
      "Produce high-quality technical blogs and solution content.",
      "Build competitive research and battlecards.",
      "Support RFP/RFI responses.",
      "Explain Synthera's solutions and their technical value clearly.",
      "Identify emerging security trends and turn them into useful technical content.",
      "Become a trusted technical resource for Sales and customers.",
    ],

    whyJoin: [
      "Work across multiple cybersecurity technologies instead of being restricted to one product.",
      "Get direct exposure to enterprise security customers.",
      "Build expertise across Zero Trust, SASE, Cloud, Data Security, and AI Security.",
      "Work on emerging areas such as Agentic AI and AI Security.",
      "Build both deep technical and customer-facing skills.",
      "Have the opportunity to grow into a Senior Solutions Engineer / Security Consultant / Technical Architect role.",
    ],

    closingStatement:
      "If you enjoy breaking down security technologies, building things hands-on, talking to customers, and constantly learning what's next in cybersecurity, we'd like to hear from you.",
  },
];

/* ================= CAREERS PAGE ================= */

export default function Careers() {
  const [selectedJob, setSelectedJob] = useState(null);

  // Filter jobs based on whether they are open or closed
  const activeJobs = jobs.filter((job) => job.isOpen);

  const buildMailto = (role) => {
    const subject = encodeURIComponent(`Application for ${role}`);

    const body = encodeURIComponent(
      `Hi Hiring Team,\n\nI'm interested in the ${role} position. Please find my resume attached.\n\nRegards`,
    );

    return `mailto:${HR_EMAIL}?subject=${subject}&body=${body}`;
  };

  return (
    <>
      <Helmet>
        <title>Careers at Synthera Security | Join Our Team</title>

        <meta
          name="description"
          content="Join Synthera Security. Explore our open positions and work across cybersecurity engineering, technical consulting, pre-sales, Zero Trust, and AI Security."
        />

        <meta
          name="keywords"
          content="Synthera Careers, Cybersecurity Jobs, Technical Consulting Engineer, Cybersecurity Consulting Jobs, Technical Pre-Sales Jobs, Security Engineering Jobs, Remote Cybersecurity Jobs, AI Security Jobs"
        />

        <meta name="robots" content="index, follow" />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/company/careers"
        />
      </Helmet>

      <div className="bg-[#f6f8fb] min-h-screen font-sans selection:bg-emerald-100 selection:text-emerald-900">
        {/* ================= JOB DETAILS MODAL ================= */}

        <AnimatePresence>
          {selectedJob && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSelectedJob(null)}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              />

              {/* Modal */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="relative bg-white w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-[2.5rem] shadow-2xl p-6 md:p-10 lg:p-12"
              >
                {/* Close Button */}
                <button
                  onClick={() => setSelectedJob(null)}
                  aria-label="Close job details"
                  className="absolute top-5 right-5 md:top-6 md:right-6 p-2 hover:bg-gray-100 rounded-full transition z-10"
                >
                  <X size={24} />
                </button>

                {/* Job Header */}
                <div className="flex items-start gap-4 mb-8 pr-10">
                  <div className="bg-emerald-50 text-emerald-600 p-3 rounded-2xl shrink-0">
                    <Briefcase size={28} />
                  </div>

                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                      {selectedJob.title}
                    </h2>

                    <p className="text-emerald-600 font-medium mt-1">
                      {selectedJob.location}
                    </p>
                  </div>
                </div>

                {/* ================= JOB META ================= */}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
                  <div className="bg-gray-50 rounded-2xl p-4">
                    <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1">
                      Experience
                    </p>

                    <p className="text-gray-900 font-semibold">
                      {selectedJob.experience}
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-2xl p-4">
                    <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1">
                      Employment Type
                    </p>

                    <p className="text-gray-900 font-semibold">
                      {selectedJob.employmentType}
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-2xl p-4 sm:col-span-2">
                    <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-1">
                      Function
                    </p>

                    <p className="text-gray-900 font-semibold">
                      {selectedJob.function}
                    </p>
                  </div>
                </div>

                {/* ================= JOB CONTENT ================= */}

                <div className="space-y-12 text-gray-600">
                  {/* About Synthera */}
                  <section>
                    <h4 className="text-xl md:text-2xl text-gray-900 font-bold mb-5">
                      About Synthera Security
                    </h4>

                    <div className="space-y-4">
                      {selectedJob.companyDesc.map((paragraph, index) => (
                        <p key={index} className="leading-relaxed">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </section>

                  {/* What You'll Do */}
                  <section>
                    <h4 className="text-xl md:text-2xl text-gray-900 font-bold mb-7">
                      What You'll Do
                    </h4>

                    <div className="space-y-9">
                      {selectedJob.responsibilities.map((section, index) => (
                        <div key={index}>
                          <h5 className="text-base md:text-lg font-bold text-gray-900 mb-4">
                            {section.title}
                          </h5>

                          <ul className="space-y-3">
                            {section.items.map((item, itemIndex) => (
                              <li key={itemIndex} className="flex gap-3">
                                <Check
                                  className="text-emerald-500 shrink-0 mt-1"
                                  size={19}
                                />

                                <span className="leading-relaxed">{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* What We're Looking For */}
                  <section>
                    <h4 className="text-xl md:text-2xl text-gray-900 font-bold mb-7">
                      What We're Looking For
                    </h4>

                    {/* Required */}
                    <div className="mb-9">
                      <h5 className="text-lg font-bold text-gray-900 mb-4">
                        Required
                      </h5>

                      <ul className="space-y-3">
                        {selectedJob.required.map((item, index) => (
                          <li key={index} className="flex gap-3">
                            <Check
                              className="text-emerald-500 shrink-0 mt-1"
                              size={19}
                            />

                            <span className="leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Good to Have */}
                    <div>
                      <h5 className="text-lg font-bold text-gray-900 mb-4">
                        Good to Have
                      </h5>

                      <ul className="space-y-3">
                        {selectedJob.goodToHave.map((item, index) => (
                          <li key={index} className="flex gap-3">
                            <Check
                              className="text-emerald-500 shrink-0 mt-1"
                              size={19}
                            />

                            <span className="leading-relaxed">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </section>

                  {/* Ideal Candidate */}
                  <section>
                    <h4 className="text-xl md:text-2xl text-gray-900 font-bold mb-5">
                      The Ideal Candidate
                    </h4>

                    <p className="leading-relaxed mb-5">
                      {selectedJob.idealCandidate.intro}
                    </p>

                    <div className="bg-gray-900 text-white rounded-2xl p-6 md:p-7 mb-6">
                      <p className="font-semibold text-base md:text-lg leading-relaxed">
                        "{selectedJob.idealCandidate.journey}"
                      </p>
                    </div>

                    <p className="leading-relaxed mb-4">
                      {selectedJob.idealCandidate.description}
                    </p>

                    <p className="leading-relaxed">
                      {selectedJob.idealCandidate.closing}
                    </p>
                  </section>

                  {/* What Success Looks Like */}
                  <section>
                    <h4 className="text-xl md:text-2xl text-gray-900 font-bold mb-5">
                      What Success Looks Like
                    </h4>

                    <p className="leading-relaxed mb-5">
                      Within your first 6 months, you should be able to:
                    </p>

                    <ul className="space-y-3">
                      {selectedJob.successLooksLike.map((item, index) => (
                        <li key={index} className="flex gap-3">
                          <Check
                            className="text-emerald-500 shrink-0 mt-1"
                            size={19}
                          />

                          <span className="leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </section>

                  {/* Why Join Synthera */}
                  <section>
                    <h4 className="text-xl md:text-2xl text-gray-900 font-bold mb-5">
                      Why Join Synthera?
                    </h4>

                    <ul className="space-y-3">
                      {selectedJob.whyJoin.map((item, index) => (
                        <li key={index} className="flex gap-3">
                          <Check
                            className="text-emerald-500 shrink-0 mt-1"
                            size={19}
                          />

                          <span className="leading-relaxed">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </section>

                  {/* Closing Statement */}
                  <section className="bg-emerald-50 rounded-2xl p-6 md:p-7">
                    <p className="text-gray-800 font-medium leading-relaxed">
                      {selectedJob.closingStatement}
                    </p>
                  </section>
                </div>

                {/* ================= APPLY ================= */}

                <div className="mt-10 pt-8 border-t border-gray-100 flex flex-col md:flex-row gap-4 items-center justify-between">
                  <p className="text-sm text-gray-500">
                    Ready to make an impact?
                  </p>

                  <a
                    href={buildMailto(selectedJob.title)}
                    className="w-full md:w-auto px-10 py-4 bg-emerald-500 text-white font-bold rounded-full hover:bg-emerald-600 transition text-center"
                  >
                    Apply Now
                  </a>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* ================= HERO ================= */}

        <section className="relative h-[55vh] md:h-[70vh] w-full overflow-hidden">
          <img
            src={heroImage}
            alt="Careers Hero"
            className="w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/90" />

          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 md:px-6">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl md:text-7xl font-extrabold text-white"
            >
              Careers at Synthera
            </motion.h1>
          </div>
        </section>

        {/* ================= MAIN CONTENT ================= */}

        <section className="-mt-12 md:-mt-32 relative z-20 px-4 md:px-8 pb-24">
          <div className="max-w-7xl mx-auto bg-white rounded-[3rem] px-6 md:px-12 py-16 shadow-xl border border-gray-100">
            <h2 className="text-3xl md:text-5xl font-bold text-center text-gray-900 mb-12">
              Open Positions
            </h2>

            {/* Conditionally render jobs or "Closed/Empty" state */}
            {activeJobs.length > 0 ? (
              <div
                id="open-positions"
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              >
                {activeJobs.map((job, index) => (
                  <motion.div
                    key={index}
                    whileHover={{ y: -10 }}
                    className="p-8 bg-white border border-gray-100 rounded-3xl shadow-sm hover:shadow-xl transition flex flex-col"
                  >
                    <div className="bg-emerald-50 text-emerald-600 w-12 h-12 rounded-xl flex items-center justify-center mb-6">
                      <Briefcase size={22} />
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {job.title}
                    </h3>

                    <p className="text-emerald-600 text-sm font-medium mb-3">
                      {job.location}
                    </p>

                    <p className="text-gray-500 text-sm mb-6 flex-1 line-clamp-3">
                      {job.shortDesc}
                    </p>

                    <button
                      onClick={() => setSelectedJob(job)}
                      className="inline-flex items-center text-emerald-600 font-bold group"
                    >
                      View Details{" "}
                      <ArrowRight
                        className="ml-2 group-hover:ml-4 transition-all"
                        size={18}
                      />
                    </button>
                  </motion.div>
                ))}
              </div>
            ) : (
              /* EMPTY / CLOSED STATE */
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-center justify-center text-center py-16 px-6 bg-gray-50 rounded-[2rem] border border-gray-100"
              >
                <div className="bg-gray-200 w-20 h-20 rounded-2xl flex items-center justify-center mb-6">
                  <Briefcase size={36} className="text-gray-400" />
                </div>

                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  No Open Positions
                </h3>

                <p className="text-gray-500 max-w-lg mx-auto mb-8 leading-relaxed text-base md:text-lg">
                  There are currently no open positions available. We are always
                  looking for talented individuals to join our team, so feel
                  free to drop us your resume for future opportunities.
                </p>

                <a
                  href={`mailto:${HR_EMAIL}?subject=General Application - Future Opportunities`}
                  className="inline-flex items-center px-8 py-4 bg-gray-900 text-white font-bold rounded-full hover:bg-emerald-600 transition-colors duration-300"
                >
                  Submit Resume
                </a>
              </motion.div>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
